import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Spinner,
  Alert,
} from "reactstrap";

import { DeliveredOrdersChart, DeliveryTimeChart } from "./Charts";
import Section from "./Breadcrumb";
import { timeConversion } from "../../../Components/Common/timeConversion";
import { useSiteStore } from "../../../stores/Assets/site";
import { useKitchenStore } from "../../../stores/Assets/kitchen";
import DataTableBase from "../../../Components/Common/DataTableBase/DataTableBase";
import moment from "moment";

const ServiceStation = () => {
  document.title = "Service Station Report - Clickorder Admin";
  
  // State management
  const [selectedSiteIds, setSelectedSiteIds] = useState([]);
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  });
  const [endDate, setEndDate] = useState(() => {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return today;
  });
  const [timezone, setTimezone] = useState(() => {
    // Capture user's timezone
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  });
  const [siteList, setSiteList] = useState([]);
  const [error, setError] = useState(null);
  
  const { isLoading, getServiceStationStats, stationReport } = useKitchenStore(
    (state) => state
  );
  const { getSites, sites } = useSiteStore((state) => state);

  // Get report data
  const getReport = async () => {
    setError(null);
    
    try {
      if (!startDate || !endDate) {
        setError("Please select a date range");
        return;
      }

      if (selectedSiteIds.length === 0) {
        setError("Please select at least one site");
        return;
      }

      // Call the store method with timezone
      await getServiceStationStats(selectedSiteIds, startDate, endDate, timezone);
    } catch (err) {
      console.error("Error fetching report:", err);
      setError("Failed to fetch report data: " + (err.message || err));
    }
  };

  // Fetch sites on component mount
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const list = await getSites();
        const formattedOptions = list?.map((site) => ({
          label: `${site.site_id} - ${site.name}`,
          value: site.site_id,
        }));
        setSiteList(formattedOptions);
      } catch (err) {
        console.error("Error fetching sites:", err);
        setError("Failed to load sites");
      }
    };

    fetchSites();
  }, [getSites]);

  // Update site list when sites store changes and select first site by default
  useEffect(() => {
    const formattedOptions = sites?.map((site) => ({
      label: `${site.site_id} - ${site.name}`,
      value: site.site_id,
    }));
    setSiteList(formattedOptions);
    
    // Select first site by default if not already selected
    if (formattedOptions && formattedOptions?.length > 0 && selectedSiteIds?.length === 0) {
      setSelectedSiteIds([formattedOptions[0].value]);
    }
  }, [sites]);

  // Auto-generate report when first site is selected
  const [hasAutoGenerated, setHasAutoGenerated] = useState(false);
  
  useEffect(() => {
    const autoGenerateReport = async () => {
      if (selectedSiteIds?.length > 0 && siteList?.length > 0 && !hasAutoGenerated) {
        // Only auto-generate once when first site is selected
        setHasAutoGenerated(true);
        await getReport();
      }
    };
    
    autoGenerateReport();
  }, [selectedSiteIds, siteList]);

  // if (isLoading) return <div>Loading...</div>; // Show loading state while fetching data
  const timeFormat = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  };

  const columns = [
    {
      name: "Site",
      selector: (row) => row.site_name,
      sortable: true,
      wrap: true,
      cell: (row) => `${row.site_id} - ${row.site_name}`,
    },
    {
      name: "Service Station",
      selector: (row) => row.kitchen_name,
      sortable: true,
      wrap: true,
      cell: (row) => `${row.kitchen_id} - ${row.kitchen_name}`,
    },
    {
      name: "Delivered Orders",
      selector: (row) => row.delivered_orders,
      sortable: true,
      center: true,
    },
    {
      name: "Avg Time",
      selector: (row) => row.avg_delivery_time,
      cell: (row) => timeFormat(row.avg_delivery_time),
      center: true,
    },
    {
      name: "Min Time",
      selector: (row) => row.min_delivery_time,
      cell: (row) => timeFormat(row.min_delivery_time),
      center: true,
    },
    {
      name: "Max Time",
      selector: (row) => row.max_delivery_time,
      cell: (row) => timeFormat(row.max_delivery_time),
      center: true,
    },
  ];

  return (
    <React.Fragment>
      <Container fluid>
        <Section
          getDailyReport={getReport}
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          timezone={timezone}
          setTimezone={setTimezone}
          selectedSiteIds={selectedSiteIds}
          setSelectedSiteIds={setSelectedSiteIds}
          siteList={siteList}
        />
        
        {error && (
          <Row className="mb-4">
            <Col xs={12}>
              <Alert color="danger">
                <i className="ri-error-warning-line me-2"></i>
                {error}
              </Alert>
            </Col>
          </Row>
        )}

        {siteList?.length === 0 ? (
          <Row className="justify-content-center">
            <Col xs="auto">
              <div className="text-center">
                <Spinner color="primary" />
                <p className="mt-3 text-muted">Loading sites...</p>
              </div>
            </Col>
          </Row>
        ) : isLoading ? (
          <Row className="justify-content-center">
            <Col xs="auto">
              <Spinner color="primary" />
              <span className="ms-2">Loading report data...</span>
            </Col>
          </Row>
        ) : stationReport && stationReport?.length > 0 ? (
          <>
            <Row>
              <Col lg={6}>
                <Card>
                  <CardHeader>
                    <h4 className="card-title mb-0">
                      Service Stations - Delivered Orders
                    </h4>
                  </CardHeader>
                  <CardBody>
                    <DeliveredOrdersChart
                      dailyStats={stationReport}
                      dataColors='["#ce9558", "--vz-info", "--vz-success"]'
                    />
                  </CardBody>
                </Card>
              </Col>

              <Col lg={6}>
                <Card>
                  <CardHeader>
                    <h4 className="card-title mb-0">
                      Service Stations - Delivery Time
                    </h4>
                  </CardHeader>
                  <CardBody>
                    <DeliveryTimeChart
                      dailyStats={stationReport}
                     dataColors='["--vz-success","#ce9558" , "--vz-danger", "--vz-dark", "--vz-primary", "--vz-success", "--vz-secondary"]'
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
            <Row>
              <Col lg={12}>
                <Card>
                  <CardHeader>
                    <h4 className="card-title mb-0">
                      Detailed Report{" "}
                      {`(${moment(startDate).format(
                        "DD/MM/YYYY hh:mm"
                      )} - ${moment(endDate).format("DD/MM/YYYY hh:mm")})`}
                    </h4>
                  </CardHeader>
                  <CardBody>
                    <DataTableBase
                      tableTitle="Service Stations Statistics"
                      data={stationReport || []}
                      columns={columns}
                      loading={isLoading}
                      paginationTotalRows={(stationReport || [])?.length}
                      showActionColumn={false}
                      showSubHeader={false}
                      showActionButtons={false}
                    />
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </>
        ) : (
          <Row className="justify-content-center">
            <Col xs="auto">
              <div className="text-center">
                <i className="ri-file-chart-line text-muted" style={{ fontSize: "4rem" }}></i>
                <h5 className="mt-3 text-muted">No data to display</h5>
                <p className="text-muted">Click Filter to generate report for the selected site</p>
              </div>
            </Col>
          </Row>
        )}
      </Container>
    </React.Fragment>
  );
};

export default ServiceStation;
